import { useEffect, useRef, useState } from 'react';
import { ARENA_HALF } from '../../../shared/constants';
import { classByIndex, getClass, upcomingEvolutions } from '../../../shared/catalog/classes';
import { MAX_LEVEL, STAT_CAP } from '../../../shared/constants';
import { STATS } from '../../../shared/stats';
import { levelForXp, xpForLevel } from '../../../shared/xp';
import { getMap } from '../../../shared/maps';
import { colors, css } from '../game/colors';
import { ARCH_BY_INDEX } from '../../../shared/monsters';
import type { WorldInfo } from '../../../shared/protocol';
import type { GameClient } from '../game/GameClient';
import type { HudState } from '../game/store';
import { formatNumber, t, type Key } from '../i18n';
import { EquippedMods, ModPanel } from './ModPanel';
import { RouletteReveal, RouletteWidget, rouletteDone } from './Roulette';
import { HudPanel, useHudPanels } from './HudPanel';
import { LobbyPanel, MatchResult, ModeHud, QuickBar } from './ModeHud';
import { classDesc, className, familyName } from '../i18n/catalog';
import { Icon } from './icons';
import { TankImage } from './TankPreview';
import { AdminPanel } from './AdminPanel';

const STAT_COLORS = ['#5cf08a', '#7fe3ff', '#ff8a5c', '#ffd24a', '#ff5a7a', '#c08aff', '#5ab0ff', '#ffb04a'];
const TOAST_ICON = { kill: 'target', level: 'pass', evo: 'tree', info: 'alert' } as const;

export function Hud({
  state,
  client,
  onOverlay,
  onQuit,
}: {
  state: HudState;
  client: GameClient;
  onOverlay: (o: 'settings' | 'tree' | 'help') => void;
  onQuit: () => void;
}) {
  const me = state.me;
  // Téléphone : une seule feuille ouverte à la fois, à gauche (stats ou roulette), jamais au milieu de l'écran.
  const [sheet, setSheet] = useState<'stats' | 'roulette' | null>(null);
  const toggleSheet = (k: 'stats' | 'roulette') => setSheet((cur) => (cur === k ? null : k));
  // « Plus tard » replie l'évolution (ou l'amélioration) en un bouton, au lieu de la cacher jusqu'au niveau suivant.
  const [evoHiddenAt, setEvoHiddenAt] = useState(0);
  const [modHiddenAt, setModHiddenAt] = useState(0);
  const compact = state.touch;
  // Panneaux repliables : ouverts par défaut sur PC ; sur téléphone fermés (sauf la mini-carte), en accordéon.
  const [tr, toggleTr] = useHudPanels(compact, { lb: !compact, world: !compact, map: true }, compact);
  const [side, toggleSide] = useHudPanels(compact, { stats: true, roulette: false });
  const evoShown = !!me && me.evo.length > 0;
  const evoOpen = evoShown && evoHiddenAt !== me!.level;
  const modShown = !!me && (!!me.slot || !!me.modSlot) && !evoOpen;
  const modOpen = modShown && modHiddenAt !== me!.level;
  const minimap = me && (
    <HudPanel open={tr.map} onToggle={() => toggleTr('map')} title={t('minimap')} className="minimap-panel">
      <Minimap
        x={me.x}
        y={me.y}
        mapId={state.world?.map ?? 'A'}
        monsters={me.info.includes('zoneIntel') ? me.intel?.monsters : undefined}
        boss={me.info.includes('bossCompass') ? me.intel?.boss : undefined}
        half={state.world?.half ?? ARENA_HALF}
        allies={me.allies}
        points={state.world?.mode?.points}
      />
    </HudPanel>
  );

  return (
    <div className={`hud ${compact ? 'hud-touch' : ''} ${state.world?.mode && state.world.mode.kind !== 'ffa' ? 'hud-mode' : ''}`}>
      {state.net !== 'open' && (
        <div className="net-banner" role="status">
          {state.net === 'version' ? t('serverVersion') : state.net === 'connecting' ? t('connecting') : t('disconnected')}
        </div>
      )}

      <div className="hud-tl">
        <div className="hud-row">
          <button type="button" className="icon-btn" aria-label={t('settings')} title={t('settings')} onClick={() => onOverlay('settings')}>
            <Icon name="settings" size={18} />
          </button>
          <button type="button" className="icon-btn" aria-label={t('classTree')} title={t('classTree')} onClick={() => onOverlay('tree')}>
            <Icon name="tree" size={18} />
          </button>
          {/* Sans flèche, React passait l'événement du clic comme « raison » : le hangar affichait « leave_[object Object] ». */}
          <button type="button" className="icon-btn" aria-label={t('back')} title={t('back')} onClick={() => onQuit()}>
            <Icon name="exit" size={18} />
          </button>
          <button type="button" className={`chip ${state.autofire ? 'chip-on' : ''}`} aria-pressed={state.autofire} onClick={() => client.setAutofire(!state.autofire)}>
            <Icon name="target" size={14} />
            {compact ? t('autofire') : state.autofire ? t('autofireOn') : t('autofireOff')}
          </button>
        </div>
        {me && (
          <div className="score-box">
            <span className="score-label">{t('score')}</span>
            <span className="score-value" data-testid="score">
              {formatNumber(me.score)}
            </span>
            {state.rank > 0 && <span className="rank">{t('rank', { r: state.rank, n: state.total })}</span>}
          </div>
        )}
        <div className="perf">
          {t('ping', { n: state.ping })} · {t('fps', { n: state.fps })} · {state.kbps} KB/s
        </div>
        {compact && me && (
          <div className="hud-row">
            <button type="button" className={`chip chip-stats ${me.pts > 0 ? 'has-points' : ''}`} aria-expanded={sheet === 'stats'} onClick={() => toggleSheet('stats')}>
              {t('stats')} {me.pts > 0 && <span className="nav-badge num">+{me.pts}</span>}
            </button>
            {me.roulette && (
              <button type="button" className={`chip chip-roulette ${me.roulette.eligible ? 'has-points' : ''}`} aria-expanded={sheet === 'roulette'} onClick={() => toggleSheet('roulette')}>
                {t('rouletteShort')} <span className="num">{rouletteDone(me.roulette)}/4</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="hud-tr">
        <HudPanel
          open={tr.lb}
          onToggle={() => toggleTr('lb')}
          title={t('leaderboard')}
          summary={state.rank > 0 ? t('rank', { r: state.rank, n: state.total }) : undefined}
          className="leaderboard"
          testId="leaderboard"
        >
          <Leaderboard state={state} />
        </HudPanel>
        <AdminPanel state={state} client={client} />
        {state.world && (state.world.mode?.kind === 'ffa' || state.world.mode?.kind === 'forge') && <WorldPanel world={state.world} since={state.worldAt} open={tr.world} onToggle={() => toggleTr('world')} />}
        {compact && minimap}
      </div>
      {/* Sur PC, la mini-carte reste en bas à droite, hors de la colonne des panneaux. */}
      {!compact && minimap}

      {state.world?.mode && <ModeHud info={state.world.mode} myTeam={state.myTeam} />}
      {state.world?.mode && state.world.mode.phase === 'ended' && <MatchResult info={state.world.mode} myTeam={state.myTeam} onLeave={() => client.leave('')} />}
      {state.lobby && <LobbyPanel lobby={state.lobby} client={client} />}
      {me && state.world?.mode && state.world.mode.kind !== 'ffa' && state.world.mode.kind !== 'forge' && !compact && <QuickBar client={client} />}

      <div className="toasts" role="status" aria-live="polite">
        {state.toasts.map((x) => (
          <div key={x.id} className={`toast toast-${x.kind}`}>
            <Icon name={TOAST_ICON[x.kind]} size={15} />
            {x.text}
          </div>
        ))}
      </div>

      {(evoShown && !evoOpen) || (modShown && !modOpen) ? (
        <div className="hud-pending">
          {evoShown && !evoOpen && (
            <button type="button" className="chip chip-pending" onClick={() => setEvoHiddenAt(0)} data-testid="evo-chip">
              <Icon name="tree" size={14} />
              {t('evolveChip', { n: me!.evo.length })}
            </button>
          )}
          {modShown && !modOpen && (
            <button type="button" className="chip chip-pending" onClick={() => setModHiddenAt(0)} data-testid="mod-chip">
              <Icon name="up" size={14} />
              {t('modChip')}
            </button>
          )}
        </div>
      ) : null}

      {me && evoOpen && (
        <div className="evo-panel" data-testid="evo-panel">
          <div className="evo-title">{t('evolveTitle')}</div>
          <div className="evo-cards">
            {me.evo.map((id) => {
              const c = getClass(id);
              return (
                <button key={id} type="button" className="evo-card" onClick={() => client.evolve(id)} data-testid={`evo-${id}`}>
                  <TankImage cls={c} look={{ code: 1 }} size={compact ? 28 : 44} className="evo-art" />
                  <span className="evo-text">
                    <span className="evo-family">{familyName(c.family)}</span>
                    <span className="evo-name">{className(c)}</span>
                    {!compact && <span className="evo-desc">{classDesc(c)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
          <button type="button" className="btn btn-ghost btn-small" onClick={() => setEvoHiddenAt(me.level)}>
            {t('evolveLater')}
          </button>
        </div>
      )}

      {me && modOpen && <ModPanel me={me} onPick={(id) => client.equipMod(id)} onLater={() => setModHiddenAt(me.level)} />}

      {me && state.rearAlert && (
        <div className="rear-alert" role="img" aria-label={t('info_rearAlert')}>
          <Icon name="alert" size={28} />
        </div>
      )}
      {me && me.info.includes('telemetry') && state.telemetry !== null && <div className="telemetry">{t('telemetry', { n: state.telemetry })}</div>}

      {me && !compact && (
        <HudPanel
          open={side.stats}
          onToggle={() => toggleSide('stats')}
          title={t('stats')}
          summary={me.pts > 0 ? <span className="nav-badge num">+{me.pts}</span> : undefined}
          className={`stats-panel ${me.pts > 0 ? 'has-points' : ''}`}
          testId="stats-panel"
        >
          <StatsBody state={state} client={client} />
        </HudPanel>
      )}
      {me && compact && sheet && (sheet === 'stats' || me.roulette) && (
        <div className={`touch-sheet ${sheet === 'stats' ? 'stats-panel' : 'roulette-sheet'}`} data-testid={sheet === 'stats' ? 'stats-panel' : undefined}>
          <div className="touch-sheet-head">
            <span className="hud-panel-title">{sheet === 'stats' ? t('stats') : t('roulette')}</span>
            <button type="button" className="touch-sheet-close" aria-label={t('close')} data-testid="sheet-close" onClick={() => setSheet(null)}>
              <Icon name="close" size={14} />
            </button>
          </div>
          {sheet === 'stats' ? <StatsBody state={state} client={client} /> : <RouletteWidget r={me.roulette!} onCashout={() => client.cashout()} embedded />}
        </div>
      )}

      {me && <BottomBars state={state} />}
      {me && state.wallet && <TokenBar state={state} client={client} />}

      {me && me.roulette && !compact && (
        <HudPanel open={side.roulette} onToggle={() => toggleSide('roulette')} title={t('roulette')} summary={`${rouletteDone(me.roulette)} / 4`} className="roulette-panel">
          <RouletteWidget r={me.roulette} onCashout={() => client.cashout()} embedded />
        </HudPanel>
      )}
      {state.death && !state.reveal && <DeathScreen state={state} onRespawn={() => client.respawn()} onRestore={() => client.restore()} />}
      {state.reveal && <RouletteReveal result={state.reveal.result} detail={state.reveal.detail} onClose={() => client.closeReveal()} />}
      {compact && <div className="landscape-hint">{t('landscapeHint')}</div>}
    </div>
  );
}

/** Points de stats et améliorations équipées (panneau « Stats »). */
function StatsBody({ state, client }: { state: HudState; client: GameClient }) {
  const me = state.me!;
  return (
    <>
          {me.pts > 0 && <div className="stats-points">{t('statPoints', { n: me.pts })}</div>}
          <EquippedMods ids={[...(me.modules ?? []), ...me.mods]} />
          {STATS.map((s, i) => (
            <div key={s} className="stat-row">
              <span className="stat-key">{i + 1}</span>
              <span className="stat-name">{t(`stat_${s}` as Key)}</span>
              <span className={(me.cap ?? STAT_CAP) > STAT_CAP ? 'stat-pips stat-pips-wide' : 'stat-pips'}>
                {Array.from({ length: me.cap ?? STAT_CAP }, (_, k) => (
                  <i key={k} style={{ background: k < me.stats[i] ? STAT_COLORS[i] : undefined }} />
                ))}
              </span>
              <button
                type="button"
                className="stat-plus"
                disabled={me.pts <= 0 || me.stats[i] >= (me.cap ?? STAT_CAP)}
                onClick={() => client.upgradeStat(i)}
                aria-label={`+ ${t(`stat_${s}` as Key)}`}
              >
                +
              </button>
            </div>
          ))}
    </>
  );
}

/** Jetons utilisables en FFA : boosts (non cumulables, 2 par vie) et réinitialisation (hors combat). */
function TokenBar({ state, client }: { state: HudState; client: GameClient }) {
  const me = state.me!;
  const w = state.wallet!;
  const [confirmReset, setConfirmReset] = useState(false);
  const has = (k: string) => (w[k] ?? 0) > 0;
  if (!has('tok_x2') && !has('tok_x5') && !has('tok_reset') && !me.boost) return null;
  return (
    <div className="token-bar">
      {me.boost && <span className="banner banner-bonus">{t('boostActive', { k: me.boost.kind === 'tok_x2' ? '×2' : '×5', s: me.boost.left })}</span>}
      {has('tok_x2') && (
        <button className="chip" disabled={!!me.boost || !!me.bonus || me.boostsLeft <= 0} onClick={() => client.useBoost('tok_x2')}>
          ×2 ({w.tok_x2})
        </button>
      )}
      {has('tok_x5') && (
        <button className="chip" disabled={!!me.boost || !!me.bonus || me.boostsLeft <= 0} onClick={() => client.useBoost('tok_x5')}>
          ×5 ({w.tok_x5})
        </button>
      )}
      {has('tok_reset') && !me.resetUsed && (
        confirmReset ? (
          <span className="hud-row">
            <span className="wip">{t('resetConfirm')}</span>
            <button
              className="chip chip-on"
              onClick={() => {
                setConfirmReset(false);
                client.resetBuild();
              }}
            >
              {t('confirm')}
            </button>
            <button className="chip" onClick={() => setConfirmReset(false)}>
              {t('cancel')}
            </button>
          </span>
        ) : (
          <button className="chip" onClick={() => setConfirmReset(true)}>
            {t('resetBtn', { n: w.tok_reset })}
          </button>
        )
      )}
    </div>
  );
}

function BottomBars({ state }: { state: HudState }) {
  const me = state.me!;
  const cls = getClass(me.cls);
  const lvlXp = xpForLevel(me.level);
  const next = xpForLevel(me.level + 1);
  const atMax = levelForXp(me.xp, me.maxLv ?? MAX_LEVEL) >= (me.maxLv ?? MAX_LEVEL);
  const prog = atMax ? 1 : (me.xp - lvlXp) / (next - lvlXp);
  const upcoming = upcomingEvolutions(me.cls);
  const nextLevel = upcoming.length ? Math.min(...upcoming.map((c) => c.level).filter((l) => l > me.level), Infinity) : Infinity;
  const w = cls.weapon;
  return (
    <div className="bottom-bars">
      {me.prot > 0 && <div className="banner banner-prot">{t('protected', { s: Math.ceil(me.prot) })}</div>}
      {me.bonus && <div className="banner banner-bonus">{t('bonusActive', { s: me.bonus.left, xp: me.bonus.envelope })}</div>}
      {me.shield && me.shield[1] > 0 && (
        <div className="mini-bar">
          <span>{t('shield')}</span>
          <div className="bar">
            <div className="fill fill-shield" style={{ width: `${Math.min(100, (me.shield[0] / me.shield[1]) * 100)}%` }} />
          </div>
        </div>
      )}
      {(w.heat || w.charge) && (
        <div className="mini-bar">
          <span>{w.charge ? t('charge') : t('heat')}</span>
          <div className="bar">
            <div className={`fill ${w.charge ? 'fill-charge' : 'fill-heat'}`} style={{ width: `${(w.charge ? me.charge : me.heat) * 100}%` }} />
          </div>
        </div>
      )}
      <div className="bar bar-hp" aria-label="HP">
        <div className="fill fill-hp" style={{ width: `${(me.hp / me.maxHp) * 100}%`, backgroundColor: css(colors().hpSelf) }} />
        <span>
          {formatNumber(me.hp)} / {formatNumber(me.maxHp)}
        </span>
      </div>
      <div className="bar bar-xp">
        <div className="fill fill-xp" style={{ width: `${prog * 100}%` }} />
        <span data-testid="level">
          {t('level', { n: me.level })} · {className(cls)}
        </span>
      </div>
      <div className="next-evo">
        {Number.isFinite(nextLevel) ? t('evolveNext', { n: nextLevel }) : upcoming.length === 0 ? t('evolveMax') : ''}
      </div>
    </div>
  );
}

function Leaderboard({ state }: { state: HudState }) {
  return (
    <>
      <ol>
        {state.top.map(([name, score, cls, bot, power], i) => (
          <li key={i}>
            <span className="lb-rank">{i + 1}</span>
            <span className="lb-name">
              {bot ? <em className="badge-bot">{t('bot')}</em> : null}
              {name}
              {power !== undefined && (
                <span className="lb-power num" title={t('forge_power')}>
                  {' '}⚙{power}
                </span>
              )}
            </span>
            <span className="lb-cls">{className(classByIndex(cls))}</span>
            <span className="lb-score">{formatNumber(score)}</span>
          </li>
        ))}
      </ol>
      <div className="lb-pop">{t('arenaPop', { h: state.humans, b: state.bots })}</div>
    </>
  );
}

function fmtClock(s: number): string {
  const v = Math.max(0, Math.round(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`;
}

/** Délai boss, état du boss et prochaine rotation. Les secondes sont extrapolées localement entre deux messages. */
function WorldPanel({ world, since, open, onToggle }: { world: WorldInfo; since: number; open: boolean; onToggle: () => void }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((x) => x + 1), 500);
    return () => clearInterval(id);
  }, []);
  const el = (performance.now() - since) / 1000;
  const b = world.boss;
  const summary = b.st === 'alive' ? `${t(`boss_${ARCH_BY_INDEX[b.kind]}` as Key)} ${Math.ceil(b.hp * 100)} %` : b.in >= 0 ? t('bossIn', { t: fmtClock(b.in - el) }) : t(`map_${world.map}` as Key);
  return (
    <HudPanel open={open} onToggle={onToggle} title={t('worldTitle')} summary={summary} className="world-panel" testId="world">
      {b.st === 'alive' ? (
        <div className="boss-line">
          <span className="boss-name">{t(`boss_${ARCH_BY_INDEX[b.kind]}` as Key)}</span>
          <div className="bar bar-boss">
            <div className="fill fill-boss" style={{ width: `${b.hp * 100}%` }} />
            <span>
              {Math.ceil(b.hp * 100)} % · {fmtClock(b.left - el)}
            </span>
          </div>
        </div>
      ) : b.in >= 0 ? (
        <div className="world-line">{t('bossIn', { t: fmtClock(b.in - el) })}</div>
      ) : null}
      <div className="world-line">
        {t(`map_${world.map}` as Key)} · {world.pend ? t('rotationPending') : t('rotationIn', { t: fmtClock(world.rot - el) })}
      </div>
    </HudPanel>
  );
}

/** Minimap : zones, frontières, coéquipiers, points de domination et position du joueur ; jamais les adversaires. */
function Minimap({
  x,
  y,
  mapId,
  monsters,
  boss,
  half,
  allies,
  points,
}: {
  x: number;
  y: number;
  mapId: string;
  monsters?: number[];
  boss?: [number, number];
  half: number;
  allies?: number[];
  points?: { x: number; y: number; owner: number }[];
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const s = c.width;
    ctx.clearRect(0, 0, s, s);
    ctx.fillStyle = 'rgba(21,26,32,0.85)';
    ctx.fillRect(0, 0, s, s);
    const map = getMap(mapId);
    const k = s / (half * 2);
    for (const r of [...map.regions].sort((a, b) => a.level - b.level)) {
      ctx.beginPath();
      for (let i = 0; i < r.poly.length; i += 2) {
        const px = (r.poly[i] + half) * k;
        const py = (r.poly[i + 1] + half) * k;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = css(colors().zoneFill[r.level]);
      ctx.globalAlpha = 0.75;
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    ctx.strokeStyle = '#39424e';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, s - 2, s - 2);
    // Renseignement de zone : monstres proches (jamais les joueurs).
    if (monsters) {
      for (let i = 0; i < monsters.length; i += 3) {
        ctx.fillStyle = ['#8d99a6', '#4fd18a', '#f0a04a', '#ff5a4a'][monsters[i + 2]] ?? '#ffffff';
        ctx.fillRect((monsters[i] + half) * k - 1.5, (monsters[i + 1] + half) * k - 1.5, 3, 3);
      }
    }
    if (boss) {
      ctx.strokeStyle = '#d6c2ff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc((boss[0] + half) * k, (boss[1] + half) * k, 5, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (points) {
      for (const p of points) {
        ctx.strokeStyle = p.owner === 0 || p.owner === 1 ? colors().teams[p.owner] : '#8d99a6';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc((p.x + half) * k, (p.y + half) * k, 6, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    if (allies) {
      ctx.fillStyle = css(colors().ally);
      for (let i = 0; i < allies.length; i += 2) {
        ctx.beginPath();
        ctx.arc((allies[i] + half) * k, (allies[i + 1] + half) * k, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    const px = (x + half) * k;
    const py = (y + half) * k;
    ctx.fillStyle = css(colors().self);
    ctx.beginPath();
    ctx.arc(px, py, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#0b0e12';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }, [x, y, mapId, monsters, boss, half, allies, points]);
  return <canvas ref={ref} className="minimap" width={140} height={140} aria-hidden />;
}

function DeathScreen({ state, onRespawn, onRestore }: { state: HudState; onRespawn: () => void; onRestore: () => void }) {
  const d = state.death!;
  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const stats: [string, string][] = [
    [t('score'), formatNumber(d.score)],
    [t('deadLevelShort'), formatNumber(d.level)],
    [t('deadTimeShort'), fmtTime(d.alive)],
    [t('deadKillsShort'), formatNumber(d.kills)],
  ];
  return (
    <div className="death" data-testid="death">
      <div className={`death-card ${d.cashout ? 'cashed' : ''}`}>
        <div className="hazard" aria-hidden />
        <h2 className="display">{d.cashout ? t('cashedTitle') : t('deadTitle')}</h2>
        {!d.cashout && <p className="death-by">{d.killer ? t('deadBy', { name: d.killerBot ? `[BOT] ${d.killer}` : d.killer }) : t('deadByEnv')}</p>}
        <dl className="death-stats">
          {stats.map(([k, v]) => (
            <div key={k}>
              <dt className="eyebrow">{k}</dt>
              <dd className="display num">{v}</dd>
            </div>
          ))}
        </dl>
        {!d.cashout && !d.team && <p className="death-bonus">{d.bonusSeconds > 0 ? t('respawnBonus', { s: d.bonusSeconds }) : t('respawnNoBonus')}</p>}
        {d.team && <p className="death-bonus">{state.world?.mode?.kind === 'survival' ? t('survivalSpectate') : t('teamRespawn')}</p>}
        {!d.cashout && (d.restoresLeft ?? 0) > 0 && (state.wallet?.tok_restore ?? 0) > 0 && (
          <button type="button" className="btn" onClick={onRestore} data-testid="restore">
            <Icon name="restore" size={16} />
            {t('restoreBtn', { n: state.wallet?.tok_restore ?? 0, r: d.restoresLeft ?? 0 })}
          </button>
        )}
        <button type="button" className="btn btn-primary" onClick={onRespawn} data-testid="respawn" autoFocus>
          <Icon name="play" size={16} />
          {t('respawnFree')}
        </button>
      </div>
    </div>
  );
}
